// Reading a horse's activity snapshot.
//
// The rules for "what counts as next", "what counts as due soon" and "is this
// horse away today" live here rather than in either component that draws them.
// The list card and the detail page ask the same questions of the same payload
// from GET /horses/activity, and two copies of a rule is one copy too many —
// the two screens quietly disagreeing about a horse's next lesson is a bug
// nobody would find without opening both at once.

import { fmtDate } from './dates';

const TYPE_LABEL = {
  coggins: 'Coggins',
  vaccination: 'Vaccination',
  farrier: 'Farrier',
  dental: 'Dental',
  vet: 'Vet',
  injection: 'Injection',
  injury: 'Injury',
  deworming: 'Deworming',
  other: 'Service',
};

export const label = (t) => TYPE_LABEL[t] ?? 'Service';

const dayOf = (iso) => Date.parse(iso + 'T00:00:00Z') / 86400000;

/** Whole days from today, negative for the past. */
export function daysFrom(today, iso) {
  return Math.round(dayOf(iso) - dayOf(today));
}

/**
 * How a barn says a date out loud.
 *
 * Inside a week people use the weekday — "she's due Thursday" — and past that
 * they use the date. Today and tomorrow get their own words because those are
 * the two that change what somebody does next.
 */
export function when(today, iso) {
  const d = daysFrom(today, iso);
  if (d === 0) return 'Today';
  if (d === 1) return 'Tomorrow';
  if (d > 1 && d < 7) return fmtDate(iso, 'weekday');
  // The year, once it isn't this one. A Coggins pulled in July 2025 rendering
  // as "Jul 31" reads as five weeks ago rather than fourteen months, and on a
  // line labeled LAST that is the whole meaning of the line.
  const sameYear = iso.slice(0, 4) === today.slice(0, 4);
  return fmtDate(iso, sameYear ? 'short' : 'medium');
}

/** "Fri, Sep 18" — the long form, for a page with room for it. */
export function longDate(iso) {
  const thisYear = new Date().getUTCFullYear() === Number(iso.slice(0, 4));
  return fmtDate(iso, thisYear ? 'dayShort' : 'dayShortYear');
}

/**
 * "Sunday, Sep 6" — the day named in full, the month short.
 *
 * The weekday is what a barn actually plans around, so it gets the whole word;
 * the month is only there to pin it down, so it doesn't. Spelling both out
 * made the headline long enough to wrap without reading any better.
 */
export function dayAndDate(iso) {
  const thisYear = new Date().getUTCFullYear() === Number(iso.slice(0, 4));
  return fmtDate(iso, thisYear ? 'dayMedium' : 'dayMediumYear');
}

/** "in 2 weeks" / "4 weeks ago" — the distance, which the date alone doesn't give. */
export function relative(today, iso) {
  const d = daysFrom(today, iso);
  if (d === 0) return 'today';
  if (d === 1) return 'tomorrow';
  if (d === -1) return 'yesterday';
  const n = Math.abs(d);
  const [size, unit] =
    n < 14 ? [n, 'day'] : n < 60 ? [Math.round(n / 7), 'week'] : [Math.round(n / 30), 'month'];
  const phrase = `${size} ${unit}${size === 1 ? '' : 's'}`;
  return d > 0 ? `in ${phrase}` : `${phrase} ago`;
}

export function clock(hhmm) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  const hour = h % 12 === 0 ? 12 : h % 12;
  const suffix = h >= 12 ? 'PM' : 'AM';
  return m === 0 ? `${hour} ${suffix}` : `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
}

/** "3d" / "5w" / "8mo" — a length that stays short enough for a lozenge. */
export function overdueFor(today, iso) {
  const days = Math.abs(daysFrom(today, iso));
  if (days < 14) return `${days}d`;
  if (days < 60) return `${Math.round(days / 7)}w`;
  return `${Math.round(days / 30)}mo`;
}

/**
 * Away right now, rather than merely entered for a show later. A horse at
 * Thermal today is the reason its stall is empty, and that's a status; a show
 * in three weeks is just the next appointment.
 */
export function isAwayNow(snapshot, today) {
  const e = snapshot?.event;
  return !!(e && e.startDate <= today && e.endDate >= today);
}

/**
 * The next appointment: a lesson, or a show the horse travels to.
 *
 * Maintenance deliberately isn't in here. A vaccination falling due is a
 * different kind of fact from a lesson at three o'clock, and letting them
 * compete for one line means whichever loses simply isn't shown.
 */
export function nextAppointment(snapshot, today) {
  if (!snapshot) return null;
  const { nextLesson, event } = snapshot;
  const eventIsNext = event && !isAwayNow(snapshot, today);
  const lessonFirst = nextLesson && (!eventIsNext || nextLesson.date <= event.startDate);

  if (lessonFirst) {
    return {
      kind: 'lesson',
      date: nextLesson.date,
      time: clock(nextLesson.startTime),
      detail: [nextLesson.riderName, nextLesson.lessonTypeName].filter(Boolean).join(' · '),
    };
  }
  if (eventIsNext) {
    return { kind: 'event', date: event.startDate, time: null, detail: event.title };
  }
  return null;
}

/**
 * The next thing due, if it's near enough to be news.
 *
 * A Coggins due next April would otherwise sit on the card all winter saying
 * the same thing every day, which is how a signal becomes furniture.
 */
export function dueSoon(snapshot, today, withinDays = 90) {
  const d = snapshot?.nextDue;
  return d && daysFrom(today, d.date) <= withinDays ? d : null;
}
